import React, { useEffect, useState } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { cx } from '@/lib/format';
import { PERMISSION_LABELS, ROLE_LABELS } from '@/lib/permissions';

const ACTION_STYLES = {
  created: 'bg-emerald-50 text-emerald-700',
  updated: 'bg-amber-50 text-amber-700',
  deleted: 'bg-red-50 text-red-600',
};

const RESOURCES = ['', 'products', 'inventory', 'orders', 'customers', 'categories', 'coupons', 'gift_finder', 'home_cms', 'settings', 'admin_users'];

const fmtSummary = (s) => {
  if (!s || typeof s !== 'object') return '';
  return Object.entries(s).slice(0, 4).map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v).slice(0, 40) : String(v).slice(0, 40)}`).join(' · ');
};

export default function AdminActivity() {
  const [data, setData] = useState(null);
  const [resource, setResource] = useState('');
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setBusy(true);
    api.get('/admin/activity', { params: { page, limit: 50, resource } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [page, resource]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Activity Log</h1>
          <p className="text-sm text-muted-foreground">A trail of every change made by admin users — orders, products, users and more.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={resource} onChange={(e) => { setResource(e.target.value); setPage(1); }} data-testid="activity-resource-filter" className="rounded-lg border border-border bg-white px-3 py-2 text-sm">
            {RESOURCES.map((r) => <option key={r} value={r}>{r ? (PERMISSION_LABELS[r] || r) : 'All modules'}</option>)}
          </select>
          <button onClick={load} disabled={busy} data-testid="activity-refresh-btn" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold disabled:opacity-60"><RefreshCw size={14} className={busy ? 'animate-spin' : ''} /> Refresh</button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="activity-log-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">When</th><th className="p-3">Who</th><th className="p-3">Action</th><th className="p-3">Module</th><th className="p-3">Details</th></tr>
          </thead>
          <tbody>
            {data?.items?.map((a) => (
              <tr key={a.id} className="border-t border-border align-top" data-testid="activity-log-row">
                <td className="whitespace-nowrap p-3 text-xs text-muted-foreground">{new Date(a.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                <td className="p-3"><p className="font-medium">{a.actor_name}</p><p className="text-[11px] text-muted-foreground">{ROLE_LABELS[a.actor_role] || a.actor_role}</p></td>
                <td className="p-3"><span className={cx('rounded-full px-2.5 py-1 text-xs font-medium capitalize', ACTION_STYLES[a.action] || 'bg-[hsl(var(--gaze-ivory))]')}>{a.action}</span></td>
                <td className="p-3 text-xs">{PERMISSION_LABELS[a.resource] || a.resource}</td>
                <td className="p-3">
                  <p className="font-mono text-[11px] text-muted-foreground">{a.method} {a.path}</p>
                  {a.summary && <p className="mt-0.5 max-w-md truncate text-xs">{fmtSummary(a.summary)}</p>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.items.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No activity recorded yet. Actions taken by admin users will appear here.</p>}
      </div>

      {data && data.pages > 1 && (
        <div className="mt-4 flex items-center justify-end gap-2 text-sm">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="activity-prev-btn" className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 disabled:opacity-50"><ChevronLeft size={14} /> Prev</button>
          <span className="text-muted-foreground">Page {data.page} of {data.pages}</span>
          <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="activity-next-btn" className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 disabled:opacity-50">Next <ChevronRight size={14} /></button>
        </div>
      )}
    </div>
  );
}
